import { FC, RefObject } from "react";
import { LoaderCircle, AlertTriangle, Settings, WifiOff } from "lucide-react";
import Message from "./Message";
import { Message as MessageType } from "@/utils/frankMessageUtils";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/contexts/LanguageContext";

interface MessageListProps {
  messages: MessageType[];
  isLoading: boolean;
  messagesEndRef: RefObject<HTMLDivElement>;
  formatTime: (date: Date) => string;
  connectionError?: string | null;
  apiStatus?: 'available' | 'unavailable' | 'unknown';
  currentApiUrl?: string;
  member?: {
    full_name: string;
    profile_picture_url?: string;
  } | null;
}

const MessageList: FC<MessageListProps> = ({ 
  messages, 
  isLoading, 
  messagesEndRef, 
  formatTime,
  connectionError,
  apiStatus,
  currentApiUrl,
  member
}) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useLanguage();
  
  const goToSettings = () => {
    navigate("/manager/admin");
  };

  const retryConnection = () => {
    window.location.reload();
  };
  
  return (
    <div className="flex-1 overflow-y-auto" style={{ minHeight: '400px' }}>
      {connectionError && (
        <Alert variant="destructive" className="mb-4 border-red-300 bg-red-50">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>{t('frank.connection_error')}</AlertTitle>
          <AlertDescription className="space-y-3">
            <p>{connectionError}</p>
            {apiStatus === 'unavailable' && (
              <div className="flex flex-wrap gap-2 items-center mt-2">
                <div className="text-xs opacity-70">Current API URL: {currentApiUrl}</div>
                <div className="flex flex-wrap gap-2 mt-2">
                  {isMobile && (
                    <Button size="sm" variant="outline" onClick={retryConnection} className="flex items-center gap-2">
                      <WifiOff className="h-4 w-4" />
                      {t('frank.retry_connection')}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={goToSettings} className="flex items-center gap-2">
                    <Settings className="h-4 w-4" />
                    {t('frank.configure_api')}
                  </Button>
                </div>
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}
      
      {messages.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
          <h3 className="text-lg font-medium mb-4 text-[#111418]">{t('frank.intro_title')}</h3>
          <p className="text-[#637588] mb-4 max-w-md">
            {t('frank.intro_description')}
          </p>
          {isMobile && (
            <div className="mt-4 text-sm p-3 bg-[#f0f2f4] rounded-md">
              <p className="text-[#637588]">{t('frank.mobile_note')}</p>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4 p-4">
          {messages.map((message) => (
            <Message 
              key={message.id} 
              message={message} 
              formatTime={formatTime} 
              member={member}
            />
          ))}
        </div>
      )}
      
      {isLoading && (
        <div className="flex justify-start p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#f0f2f4] rounded-full flex items-center justify-center">
              <LoaderCircle className="h-4 w-4 animate-spin text-[#637588]" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-[#637588] text-[13px] font-normal">AI</p>
              <div className="bg-[#f0f2f4] text-[#111418] rounded-lg px-4 py-3 max-w-[360px]">
                <span className="text-sm">{t('frank.typing')}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
